// Sesión del portal: el token propio del servicio (cst_…) vive en
// sessionStorage — la sesión es por pestaña y muere al cerrarla (a diferencia de
// la app, que conserva 90 días en el llavero). Flujo idéntico a
// src/data/FirebaseBackend.ts: resolveIdentifier (si no hay '@') →
// signInWithPassword → issueSession (con el idToken de Bearer) → session.
// El registro replica signUp de la app: accounts:signUp → accounts:update
// (displayName) → verificación de correo → issueSession → session.
//
// Verificación de correo y recuperación, dos caminos según el despliegue
// (mismo criterio que la app): el servicio solo expone las acciones de código
// (sendEmailCode, confirmEmailCode, sendResetCode, confirmResetCode) cuando
// trae correo propio — emulador/selfhost; en la nube responden INVALID_ACTION
// y quien manda el correo es Auth con sendOobCode: VERIFY_EMAIL lleva un
// enlace que marca el correo (necesita el idToken), PASSWORD_RESET un enlace
// al formulario de Firebase (necesita el email). Por eso se guarda también el
// idToken de Firebase: sirve para reenviar la verificación desde Gate.

import { api, ApiError, sendOobCode, signInWithPassword, signUpAccount, updateAccount } from './api';
import type { Session } from './types';

const KEY = 'cargados.portal.token';
// Etiqueta del dispositivo que listSessions muestra en «Mis sesiones» de la app.
const DEVICE = 'Portal web';
// El idToken de Firebase vive ~1 h: solo se usa para reenviar VERIFY_EMAIL.
const FB_TOKEN_MS = 50 * 60 * 1000;

/** Cómo llega el correo de verificación al usuario en este despliegue. */
export type VerifyMode = 'code' | 'link';

interface StoredSession { token?: unknown; fb?: unknown; fbAt?: unknown; verify?: unknown }

function readStored(): StoredSession | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSession;
    return typeof parsed?.token === 'string' && parsed.token.startsWith('cst_') ? parsed : null;
  } catch { return null; }
}

function writeSession(next: StoredSession) {
  try { sessionStorage.setItem(KEY, JSON.stringify(next)); } catch { /* sesión solo en memoria */ }
}

export function readToken(): string | null {
  return readStored()?.token as string;
}

export function clearToken() {
  try { sessionStorage.removeItem(KEY); } catch { /* sin almacenamiento */ }
}

/** idToken de Firebase fresco (<50 min) para reenviar VERIFY_EMAIL; null si
    venció — el usuario reingresa y se renueva solo. */
function firebaseToken(): string | null {
  const s = readStored();
  return typeof s?.fb === 'string' && typeof s.fbAt === 'number' && Date.now() - s.fbAt < FB_TOKEN_MS ? s.fb : null;
}

/** El modo de verificación ya detectado en este despliegue (null = aún no se
    sabe; Gate arranca en 'code' y el primer reenvío lo corrige). */
export function readVerifyMode(): VerifyMode | null {
  const v = readStored()?.verify;
  return v === 'link' ? 'link' : v === 'code' ? 'code' : null;
}

export function writeVerifyMode(mode: VerifyMode) {
  const s = readStored();
  if (s) writeSession({ ...s, verify: mode });
}

/** Correo de verificación: código de 6 dígitos si el servicio lo expone;
    INVALID_ACTION → enlace de Auth (sendOobCode VERIFY_EMAIL, igual que la app
    en la nube). */
async function sendVerification(idToken: string): Promise<VerifyMode> {
  try {
    await api('sendEmailCode', {}, idToken);
    return 'code';
  } catch (e) {
    if (!(e instanceof ApiError) || e.code !== 'INVALID_ACTION') throw e;
    await sendOobCode({ requestType: 'VERIFY_EMAIL', idToken });
    return 'link';
  }
}

/** Reenvío desde Gate: con el cst_ si el servicio trae códigos; si no, con el
    idToken guardado (vive ~1 h — después toca reingresar). */
export async function resendVerification(token: string): Promise<VerifyMode> {
  try {
    const result = await api<{ delivered?: boolean; alreadyVerified?: boolean }>('sendEmailCode', {}, token);
    if (result?.delivered === false) throw new ApiError('ALREADY_VERIFIED', 'El correo ya quedó verificado o el servicio no pudo enviarlo. Toca «Comprobar».');
    return 'code';
  } catch (e) {
    if (!(e instanceof ApiError) || e.code !== 'INVALID_ACTION') throw e;
    const fb = firebaseToken();
    if (!fb) throw new ApiError('RESEND_UNAVAILABLE', 'Para reenviar el correo de verificación cierra sesión y vuelve a entrar.');
    await sendOobCode({ requestType: 'VERIFY_EMAIL', idToken: fb });
    return 'link';
  }
}

/** Login con correo o celular. Devuelve la sesión ya validada por el servicio. */
export async function signIn(identifier: string, password: string): Promise<{ token: string; session: Session }> {
  const id = identifier.trim();
  let email = id;
  if (!id.includes('@')) {
    // Acción anónima del servicio: traduce el celular registrado a su correo.
    const resolved = await api<{ email: string }>('resolveIdentifier', { identifier: id });
    if (typeof resolved?.email !== 'string' || !resolved.email.trim()) throw new ApiError('INVALID_RESPONSE', 'El servicio no respondió correctamente.');
    email = resolved.email;
  }
  const data = await signInWithPassword(email, password);
  if (!data.idToken) throw new ApiError('INVALID_RESPONSE', 'El servicio no respondió correctamente.');
  // El token propio (cst_) reemplaza al idToken como Bearer para todo lo demás.
  const issued = await api<{ token: string; expiresAt: string }>('issueSession', { device: DEVICE }, data.idToken);
  if (typeof issued?.token !== 'string' || !issued.token.startsWith('cst_')) throw new ApiError('INVALID_RESPONSE', 'El servicio no respondió correctamente.');
  writeSession({ verify: readStored()?.verify, token: issued.token, fb: data.idToken, fbAt: Date.now() });
  const session = await api<Session>('session', {}, issued.token);
  return { token: issued.token, session };
}

/**
 * Registro abierto (misma secuencia que FirebaseBackend.signUp de la app):
 * crea la cuenta en Auth, fija el nombre, envía la verificación de correo
 * (código o enlace según el despliegue) y emite el cst_ de la pestaña. La
 * sesión queda con emailVerified=false — Gate verifica y luego joinGroup (sin
 * código: el miembro entra 'pending' a la cola del administrador).
 */
export async function signUp(email: string, password: string, displayName: string): Promise<{ token: string; session: Session }> {
  const name = displayName.trim().slice(0, 80);
  if (password.length < 12 || name.length < 2) throw new ApiError('INVALID_INPUT', 'Escribe tu nombre y una contraseña de al menos 12 caracteres.');
  const data = await signUpAccount(email.trim(), password);
  if (!data.idToken) throw new ApiError('INVALID_RESPONSE', 'El servicio no respondió correctamente.');
  // El displayName viaja por accounts:update; si renueva el idToken se usa el nuevo.
  const updated = await updateAccount(data.idToken, name);
  const bearer = typeof updated.idToken === 'string' && updated.idToken ? updated.idToken : data.idToken;
  // La verificación sale ANTES de emitir la sesión propia (mismo orden que la
  // app); un fallo aquí deja la cuenta creada — al entrar, Gate la reenvía.
  const verify = await sendVerification(bearer);
  const issued = await api<{ token: string; expiresAt: string }>('issueSession', { device: DEVICE }, bearer);
  if (typeof issued?.token !== 'string' || !issued.token.startsWith('cst_')) throw new ApiError('INVALID_RESPONSE', 'El servicio no respondió correctamente.');
  writeSession({ verify, token: issued.token, fb: bearer, fbAt: Date.now() });
  const session = await api<Session>('session', {}, issued.token);
  return { token: issued.token, session };
}

/** Recuperación de contraseña. Devuelve cómo llega el correo: 'code' = código
    de 6 dígitos del servicio (emulador/selfhost); 'link' = enlace nativo de
    Auth — en la nube las acciones de código responden INVALID_ACTION. Como en
    la app, es silenciosa si el correo no existe: no revela cuentas. */
export async function sendResetCode(email: string): Promise<VerifyMode> {
  try {
    await api('sendResetCode', { email: email.trim() });
    return 'code';
  } catch (e) {
    if (!(e instanceof ApiError) || e.code !== 'INVALID_ACTION') throw e;
    try { await sendOobCode({ requestType: 'PASSWORD_RESET', email: email.trim() }); }
    catch (e2) { if (!(e2 instanceof ApiError) || e2.code !== 'EMAIL_NOT_FOUND') throw e2; }
    return 'link';
  }
}

export async function confirmResetCode(email: string, code: string, password: string): Promise<void> {
  if (password.length < 12) throw new ApiError('INVALID_INPUT', 'Usa una contraseña de al menos 12 caracteres.');
  await api('confirmResetCode', { email: email.trim(), code: code.trim(), password });
}

/** Restaura la pestaña: valida el cst_ guardado pidiendo la sesión al servicio. */
export async function restore(): Promise<{ token: string; session: Session } | null> {
  const token = readToken();
  if (!token) return null;
  const session = await api<Session>('session', {}, token);
  return { token, session };
}

/** Cierra la sesión: revoca el cst_ en el servicio (best-effort) y limpia local. */
export async function signOut(token: string | null): Promise<void> {
  if (token) await api('revokeSession', {}, token).catch(() => {});
  clearToken();
}
